import { useCallback, useEffect, useRef, useState } from "react";
import { useSelfServiceCardReadPending } from "../context";

/**
 * Keep card actions single-flight and suppress navigation after their screen has left.
 * @since 1.1.10
 * @example
 * const { run, isPending } = useCardAction();
 */
export function useCardAction() {
  const active = useRef(false);
  const running = useRef(false);
  const [isPending, setPending] = useState(false);
  useSelfServiceCardReadPending(isPending);

  useEffect(function ownCardActionScreen() {
    active.current = true;
    return function leaveCardActionScreen() {
      active.current = false;
    };
  }, []);

  const run = useCallback(
    async <Result>(
      operation: () => Promise<Result>,
    ): Promise<Result | undefined> => {
      if (!active.current || running.current) return undefined;
      running.current = true;
      setPending(true);
      try {
        const result = await operation();
        return active.current ? result : undefined;
      } catch (error) {
        if (active.current) throw error;
        return undefined;
      } finally {
        running.current = false;
        if (active.current) setPending(false);
      }
    },
    [],
  );

  return { run, isPending };
}
